<template>
    <div class="min-h-screen w-full flex flex-col bg-gradient-to-br from-gray-100 to-blue-50">
        <div class="h-full container mx-auto">
            <div class="px-8 flex flex-col h-full w-full py-8">
                <div class="w-full bg-white rounded-2xl border shadow-lg px-6 md:px-8 py-4 flex flex-col">
                    <div class="flex items-center text-sm font-light">
                        <router-link to="/dashboard" class="opacity-50 hover:opacity-100">Dashboard</router-link>
                        <span class="mx-2">/</span>
                        <router-link to="/academic-industry-engagement-hub-management"
                            class="opacity-50 hover:opacity-100">Academic Industry Engagement Hub</router-link>
                        <span class="mx-2">/</span>
                        <span>Academic Industry Engagement Hub Submissions</span>
                    </div>
                    <div class="font-bold">Academic Industry Engagement Hub Submissions</div>
                </div>

                <section class="mt-8 h-full w-full flex flex-col bg-white rounded-2xl border shadow-lg p-6 md:p-8">
                    <div class="flex justify-between items-center gap-4">
                        <h2 class="text-2xl font-semibold text-blue-900">🎯 Submitted Engagement Ideas</h2>
                    </div>
                    <div class="flex flex-col mt-4" v-if="$apollo.queries.listAcademicIndustryEngagementHubs.loading">
                        <table-shimmer :titleHeaders="[
                            '#',
                            'Submitter',
                            'Title',
                            'Description',
                            'Attachments',
                            'Created At',
                            'Action',
                        ]" :rows="first"></table-shimmer>
                    </div>
                    <div v-else-if="!$apollo.queries.listAcademicIndustryEngagementHubs.loading && !hasItems"
                        class="h-full flex justify-center items-center">
                        <h1 class="text-gray-500 text-lg">No ideas submitted yet.</h1>
                    </div>
                    <div v-else class="flex-auto flex flex-col mt-4">
                        <div class="h-full flex flex-col overflow-x-auto">
                            <div class="inline-block min-w-full py-2">
                                <div class="overflow-x-auto">
                                    <table class="min-w-full text-left text-sm font-light">
                                        <thead class="border-b font-medium dark:border-neutral-500">
                                            <tr>
                                                <th scope="col" class="px-6 py-2 text-gray-400 w-8">
                                                    #
                                                </th>
                                                <th scope="col" class="px-6 py-2 text-gray-400">
                                                    Submitter
                                                </th>
                                                <th scope="col" class="px-6 py-2 text-gray-400">
                                                    Title
                                                </th>
                                                <th scope="col" class="px-6 py-2 text-gray-400">
                                                    Description
                                                </th>
                                                <th scope="col" class="px-6 py-2 text-gray-400">
                                                    Attachments
                                                </th>
                                                <th scope="col" class="px-6 py-2 text-gray-400">
                                                    Created At
                                                </th>
                                                <th scope="col" class="px-6 py-2 text-gray-400">
                                                    Action
                                                </th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            <tr v-for="(item, index) in items" :key="index"
                                                class="border-b border-dashed dark:border-neutral-500">
                                                <td class="whitespace-nowrap px-6 py-2 font-medium">
                                                    {{ paginatorInfo.firstItem + index }}
                                                </td>
                                                <td class="whitespace-nowrap px-6 py-2">
                                                    <div class="flex flex-col">
                                                        <h1>{{ item.user.name }}</h1>
                                                        <p class="text-xs text-gray-600 capitalize">
                                                            ({{ item.created_by_role == 'user' ? 'Student' :
                                                                item.created_by_role }})
                                                        </p>
                                                        <p class="text-xs text-gray-400">
                                                            {{ item.user.email }}
                                                        </p>
                                                        <p class="text-xs text-gray-400">
                                                            {{ item.user.phone_number }}
                                                        </p>
                                                    </div>
                                                </td>
                                                <td class="px-6 py-2">
                                                    {{ item.title }}
                                                </td>
                                                <td class="px-6 py-2 max-w-sm" :title="item.description">
                                                    <div class="truncate-3">{{ item.description }}</div>
                                                </td>
                                                <td class="px-6 py-2 whitespace-nowrap">
                                                    {{ item.attachments.length ? `${item.attachments.length} files` :
                                                        'No' }}
                                                </td>
                                                <td class="px-6 py-2 whitespace-nowrap">
                                                    {{ getFormattedDate(item.created_at) }}
                                                </td>
                                                <td class="px-6 py-2">
                                                    <div class="flex">
                                                        <div @click.stop="viewItem(item)"
                                                            class="bg-gray-100 hover:bg-blue-50 w-8 h-8 flex justify-center items-center rounded-lg group cursor-pointer mr-2">
                                                            <icon-eye
                                                                class="w-4 h-4 transform group-hover:text-purple-pink group-hover:scale-110 hover:scale-110"></icon-eye>
                                                        </div>
                                                    </div>
                                                </td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>
                        <div class="mt-4">
                            <pagination :paginatorInfo="paginatorInfo" :setPage="setPage"></pagination>
                        </div>

                    </div>

                </section>

            </div>
        </div>

        <transition name="fade" mode="out-in">
            <base-full-modal v-if="isViewItem" :title="`View Academic Industry Engagement Hub`" height=""
                @close="closeModal">
                <academic-industry-engagement-hub-view class="text-left" :item="selectedItem"
                    @close="closeModal"></academic-industry-engagement-hub-view>
            </base-full-modal>
        </transition>

        <div class="h-full flex justify-center items-center" v-if="isLoading">
            <base-page-spinner type="spin" class-list="h-20"></base-page-spinner>
        </div>

    </div>
</template>

<script>
import gql from "graphql-tag";
import Pagination from "../../components/shared/pagination.vue";
import TableShimmer from "../../components/shared/TableShimmer.vue";
import AcademicIndustryEngagementHubView from "./view.vue";
import IconEye from "../../components/icons/IconEye.vue";
import { formattedDate } from "../../helpers/utils";

const LIST_ACADEMIC_INDUSTRY_ENGAGEMENT_HUB_QUERY = gql`
  query ListThinkTanks($first: Int!, $page: Int!) {
    listAcademicIndustryEngagementHubs(first: $first, page: $page) {
      error
      error_code
      message
      paginatorInfo {
        count
        currentPage
        firstItem
        hasMorePages
        lastItem
        lastPage
        perPage
        total
      }
      data {
        user {
            name
            email
            phone_number
            profile_picture_url
            entity
            campuses
            role_display_name
            userDepartments {
                department
            }
        }
        title
        description
        engagement_type
        date_of_engagement
        target_outcome
        funded
        funding_details
        expected_outcome
        mentor
        industry_partner
        created_at
        created_by_role
        attachments {
          file_path
          file_name
        }
        kpi_pillar_id
        kpi_sub_section_id
        kpiPillar {
          id
          title
          weight
        }
        kpiSubSection {
          id
          title
          kpi_code
        }
      }
    }
  }
`;

export default {
    name: "academic-industry-engagement-hub-submissions",
    components: {
        Pagination,
        TableShimmer,
        AcademicIndustryEngagementHubView,
        IconEye,
    },
    data() {
        return {
            first: 10,
            page: 1,
            listAcademicIndustryEngagementHubs: null,
            isLoading: false,
            isViewItem: false,
            selectedItem: null,
        };
    },
    computed: {
        items() {
            return this.listAcademicIndustryEngagementHubs.data;
        },
        hasItems() {
            if (this.items && this.items.length > 0) {
                return true;
            } else {
                return false;
            }
        },
        paginatorInfo() {
            return this.listAcademicIndustryEngagementHubs.paginatorInfo;
        },
    },
    apollo: {
        listAcademicIndustryEngagementHubs() {
            return {
                query: LIST_ACADEMIC_INDUSTRY_ENGAGEMENT_HUB_QUERY,
                variables() {
                    return {
                        first: this.first,
                        page: this.page,
                    }
                },
                fetchPolicy: 'cache-and-network'
            }
        },
    },
    methods: {
        setPage(page) {
            this.page = page;
        },
        viewItem(item) {
            this.selectedItem = item;
            this.isViewItem = true;
        },
        closeModal() {
            this.isViewItem = false;
            this.selectedItem = null;
        },
        getFormattedDate(date) {
            return formattedDate(date);
        }
    },
};
</script>

<style lang="scss" scoped></style>
